/**
 * Eén manier om beelden te tonen: drie formaten via srcset, expliciete afmetingen
 * zodat de bladspiegel niet verspringt, en een verplichte beschrijving.
 */
import { useState } from "react";
import { deliveryVariants } from "@/lib/image-delivery";
import { focalToObjectPosition, type FocalPoint } from "@/lib/media-taxonomy";

export type ResponsiveVariant = { width: number; height: number; url: string };

export type ResponsiveImageProps = {
  src: string;
  alt: string;
  variants?: ResponsiveVariant[];
  width?: number;
  height?: number;
  /** Vaste verhouding volgens het merkboek. */
  ratio?: "16:9" | "4:5";
  /** Het eerste beeld van een pagina laadt meteen, de rest pas bij het scrollen. */
  priority?: boolean;
  sizes?: string;
  className?: string;
  /** Beeld uit de eigen opslag, getoond als de hoofdbron (Cloudflare) niet antwoordt. */
  fallbackSrc?: string | null;
  /** Brandpunt uit de bibliotheek: elke uitsnede houdt het onderwerp in beeld. */
  focal?: FocalPoint | null;
  /** Sierbeeld: een lege beschrijving, zodat schermlezers het overslaan. */
  decorative?: boolean;
  /** Gemiddelde kleur van de foto (#rrggbb), zichtbaar terwijl de foto laadt. */
  placeholderColor?: string | null;
};

const RATIOS: Record<NonNullable<ResponsiveImageProps["ratio"]>, string> = {
  "16:9": "aspect-[16/9]",
  "4:5": "aspect-[4/5]",
};

export function ResponsiveImage({
  src,
  alt,
  variants = [],
  width,
  height,
  ratio,
  priority = false,
  sizes = "(min-width: 1280px) 33vw, (min-width: 640px) 50vw, 100vw",
  className = "",
  fallbackSrc = null,
  focal = null,
  decorative = false,
  placeholderColor = null,
}: ResponsiveImageProps) {
  const [failed, setFailed] = useState(false);
  const useFallback = failed && Boolean(fallbackSrc) && fallbackSrc !== src;
  const all = deliveryVariants(src, variants);
  const sorted = useFallback ? [] : all;
  // Afmetingen blijven die van de foto, ook bij de terugval: geen verspringende pagina.
  const largest = all[all.length - 1];
  const srcSet = sorted.length > 1 ? sorted.map((variant) => `${variant.url} ${variant.width}w`).join(", ") : undefined;

  return (
    <img
      src={useFallback ? (fallbackSrc as string) : src}
      {...(srcSet ? { srcSet, sizes } : {})}
      {...(fallbackSrc && !useFallback ? { onError: () => setFailed(true) } : {})}
      alt={decorative ? "" : alt}
      {...(decorative ? { role: "presentation" as const } : {})}
      {...(focal || placeholderColor
        ? {
            style: {
              ...(focal ? { objectPosition: focalToObjectPosition(focal) } : {}),
              ...(placeholderColor ? { backgroundColor: placeholderColor } : {}),
            },
          }
        : {})}
      width={width ?? largest?.width ?? 1024}
      height={height ?? largest?.height ?? 768}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      decoding="async"
      className={`${ratio ? RATIOS[ratio] : ""} h-full w-full object-cover ${className}`.trim()}
    />
  );
}
